'use client';

/**
 * FileRenderer Component
 *
 * Renders FILE, IMAGE, and SIGNATURE field types.
 * Supports display, edit, and inline modes.
 */

import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import { getNeonStyles, type NeonAccent } from '../../../theme/palette';
import { glassEffects } from '../../../theme/shadows';
import { transitionPresets } from '../../../theme/transitions';
import type { FieldDefinition, FieldMode } from '../../../types/entityFramework';
import { formatBytes } from '../../../utils/formatters';
import { transientOptions } from '../../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface FileValue {
  id: string;
  name: string;
  url: string;
  size?: number;
  mimeType?: string;
}

export interface FileRendererProps {
  /** Field definition */
  field: FieldDefinition;
  /** Current value (FileValue or FileValue[]) */
  value: unknown;
  /** Render mode */
  mode: FieldMode;
  /** Change handler */
  onChange?: (value: unknown) => void;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Additional class name */
  className?: string;
  /** Upload handler - returns the uploaded file metadata */
  onUpload?: (file: File) => Promise<{ id: string; url: string; name: string }>;
  /** Accepted file types (e.g., "image/*,.pdf") */
  accept?: string;
  /** Maximum file size in bytes */
  maxSize?: number;
}

// ============================================================================
// Styled Components
// ============================================================================

interface DropZoneProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $hasError: boolean;
  $isDragActive: boolean;
  $size: 'small' | 'medium' | 'large';
  $disabled: boolean;
}

const DropZone = styled(
  Box,
  transientOptions,
)<DropZoneProps>(({ theme, $isDark, $neonAccent, $hasError, $isDragActive, $size, $disabled }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  const sizeStyles = {
    small: { padding: '16px', minHeight: 80 },
    medium: { padding: '24px', minHeight: 120 },
    large: { padding: '32px', minHeight: 160 },
  };

  const s = sizeStyles[$size];

  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    border: `2px dashed ${
      $hasError
        ? theme.palette.error.main
        : $isDragActive
          ? neonStyles?.base || theme.palette.primary.main
          : neonStyles
            ? neonStyles.border
            : glass.border
    }`,
    backgroundColor: $isDragActive
      ? alpha(neonStyles?.base || theme.palette.primary.main, $isDark ? 0.1 : 0.05)
      : glass.background,
    backdropFilter: glass.backdropBlur,
    transition: transitionPresets.card,
    cursor: $disabled ? 'not-allowed' : 'pointer',
    opacity: $disabled ? 0.6 : 1,
    ...s,

    '&:hover': $disabled
      ? {}
      : {
          borderColor: $hasError ? theme.palette.error.main : neonStyles?.base || theme.palette.primary.main,
          backgroundColor: alpha(neonStyles?.base || theme.palette.primary.main, $isDark ? 0.08 : 0.04),
          boxShadow: neonStyles ? `0 0 12px ${neonStyles.glow}` : undefined,
        },
  };
});

const FileItem = styled(
  Box,
  transientOptions,
)<{
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}>(({ $isDark, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 12px',
    borderRadius: 8,
    backgroundColor: glass.background,
    border: `1px solid ${neonStyles ? neonStyles.border : glass.border}`,
    transition: transitionPresets.card,

    '&:hover': {
      backgroundColor: glass.backgroundHover,
      borderColor: neonStyles ? neonStyles.base : glass.borderHover,
    },
  };
});

const ImageThumbnail = styled(
  Box,
  transientOptions,
)<{ $isDark: boolean }>(({ $isDark }) => ({
  width: 48,
  height: 48,
  borderRadius: 8,
  overflow: 'hidden',
  backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,

  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

const SignaturePad = styled(
  Box,
  transientOptions,
)<{
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $size: 'small' | 'medium' | 'large';
}>(({ $isDark, $neonAccent, $size }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  const sizeStyles = {
    small: { height: 80 },
    medium: { height: 120 },
    large: { height: 160 },
  };

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    border: `1px solid ${neonStyles ? neonStyles.border : glass.border}`,
    backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
    ...sizeStyles[$size],

    '& img': {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain',
    },
  };
});

const DisplayValue = styled(
  Typography,
  transientOptions,
)<{ $isDark: boolean }>(({ $isDark }) => ({
  color: $isDark ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.87)',
  wordBreak: 'break-word',
}));

const InlineDropZone = styled(
  Box,
  transientOptions,
)<{
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $isDragActive: boolean;
}>(({ theme, $isDark, $neonAccent, $isDragActive }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 8px',
    borderRadius: 8,
    border: `1px dashed ${$isDragActive ? neonStyles?.base || theme.palette.primary.main : 'transparent'}`,
    backgroundColor: $isDragActive
      ? alpha(neonStyles?.base || theme.palette.primary.main, $isDark ? 0.1 : 0.05)
      : 'transparent',
    transition: transitionPresets.input,
    cursor: 'pointer',

    '&:hover': {
      backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
      borderColor: neonStyles
        ? alpha(neonStyles.border, 0.5)
        : $isDark
          ? 'rgba(255, 255, 255, 0.1)'
          : 'rgba(0, 0, 0, 0.1)',
    },
  };
});

// ============================================================================
// Helpers
// ============================================================================

const isFileValue = (value: unknown): value is FileValue => {
  return typeof value === 'object' && value !== null && 'id' in value && 'name' in value && 'url' in value;
};

const normalizeValue = (value: unknown): FileValue[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.filter(isFileValue);
  }
  if (isFileValue(value)) {
    return [value];
  }
  return [];
};

const isImageFile = (file: FileValue): boolean => {
  if (file.mimeType?.startsWith('image/')) return true;
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext || '');
};

const getDefaultAccept = (fieldType: string): string => {
  switch (fieldType) {
    case 'IMAGE':
      return 'image/*';
    case 'SIGNATURE':
      return 'image/*';
    default:
      return '*/*';
  }
};

// ============================================================================
// Component
// ============================================================================

export const FileRenderer: React.FC<FileRendererProps> = ({
  field,
  value,
  mode,
  onChange,
  error,
  disabled = false,
  size = 'medium',
  neonAccent,
  className,
  onUpload,
  accept,
  maxSize,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const files = normalizeValue(value);
  const isMultiple = field.fieldType === 'FILE';
  const isImage = field.fieldType === 'IMAGE';
  const isSignature = field.fieldType === 'SIGNATURE';
  const effectiveAccept = accept || getDefaultAccept(field.fieldType);
  const effectiveMaxSize = maxSize || field.maxFileSize;

  // Handle file selection
  const handleFiles = useCallback(
    async (selectedFiles: FileList | null) => {
      // Clear any previous validation error
      setValidationError(null);

      if (!selectedFiles || selectedFiles.length === 0 || !onUpload) return;

      const filesToProcess = isMultiple ? Array.from(selectedFiles) : [selectedFiles[0]];

      // Validate file sizes
      if (effectiveMaxSize) {
        const oversizedFile = filesToProcess.find((f) => f.size > effectiveMaxSize);
        if (oversizedFile) {
          const errorMsg = `File "${oversizedFile.name}" exceeds maximum size of ${formatBytes(effectiveMaxSize)}`;
          setValidationError(errorMsg);
          return;
        }
      }

      setIsUploading(true);

      try {
        const uploadedFiles: FileValue[] = [];

        for (const file of filesToProcess) {
          const result = await onUpload(file);
          uploadedFiles.push({
            id: result.id,
            name: result.name,
            url: result.url,
            size: file.size,
            mimeType: file.type,
          });
        }

        if (isMultiple) {
          onChange?.([...files, ...uploadedFiles]);
        } else {
          onChange?.(uploadedFiles[0]);
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Upload failed';
        setValidationError(errorMsg);
      } finally {
        setIsUploading(false);
      }
    },
    [onUpload, onChange, files, isMultiple, effectiveMaxSize],
  );

  // Handle file input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(event.target.files);
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle click on drop zone
  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle drag events
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (!disabled) {
      handleFiles(e.dataTransfer.files);
    }
  };

  // Handle file removal
  const handleRemove = (fileId: string) => {
    const remainingFiles = files.filter((f) => f.id !== fileId);
    if (isMultiple) {
      onChange?.(remainingFiles);
    } else {
      onChange?.(null);
    }
  };

  // Render file icon based on type
  const renderFileIcon = (file: FileValue) => {
    if (isImageFile(file)) {
      return (
        <ImageThumbnail $isDark={isDark}>
          <img src={file.url} alt={file.name} />
        </ImageThumbnail>
      );
    }
    return (
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <AttachFileIcon sx={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }} />
      </Box>
    );
  };

  // Display mode
  if (mode === 'display') {
    if (files.length === 0) {
      return (
        <Typography
          variant="body2"
          className={className}
          sx={{
            color: 'text.secondary',
          }}
        >
          -
        </Typography>
      );
    }

    // Signature display
    if (isSignature && files[0]) {
      return (
        <SignaturePad $isDark={isDark} $neonAccent={neonAccent} $size={size} className={className}>
          <img src={files[0].url} alt="Signature" />
        </SignaturePad>
      );
    }

    // Image display
    if (isImage && files[0]) {
      return (
        <Box className={className}>
          <ImageThumbnail $isDark={isDark} sx={{ width: 80, height: 80 }}>
            <img src={files[0].url} alt={files[0].name} />
          </ImageThumbnail>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              mt: 0.5,
            }}
          >
            {files[0].name}
            {files[0].size && ` (${formatBytes(files[0].size)})`}
          </Typography>
        </Box>
      );
    }

    // File list display
    return (
      <Box className={className} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {files.map((file) => (
          <Box key={file.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachFileIcon
              fontSize="small"
              sx={{
                color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
              }}
            />
            <DisplayValue variant="body2" $isDark={isDark}>
              {file.name}
              {file.size && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    ml: 1,
                  }}
                >
                  ({formatBytes(file.size)})
                </Typography>
              )}
            </DisplayValue>
          </Box>
        ))}
      </Box>
    );
  }

  // Hidden file input
  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept={effectiveAccept}
      multiple={isMultiple}
      onChange={handleInputChange}
      style={{ display: 'none' }}
      disabled={disabled || field.disabled}
    />
  );

  // Inline mode
  if (mode === 'inline') {
    return (
      <Box className={className}>
        {fileInput}
        {files.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {files.map((file) => (
              <Box key={file.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isImageFile(file) ? (
                  <ImageIcon fontSize="small" color="action" />
                ) : (
                  <AttachFileIcon fontSize="small" color="action" />
                )}
                <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                  {file.name}
                </Typography>
                <IconButton size="small" onClick={() => handleRemove(file.id)} disabled={disabled}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            {isMultiple && (
              <InlineDropZone
                $isDark={isDark}
                $neonAccent={neonAccent}
                $isDragActive={isDragActive}
                onClick={handleClick}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <CloudUploadIcon fontSize="small" color="action" />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Add more
                </Typography>
              </InlineDropZone>
            )}
          </Box>
        ) : (
          <InlineDropZone
            $isDark={isDark}
            $neonAccent={neonAccent}
            $isDragActive={isDragActive}
            onClick={handleClick}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <CloudUploadIcon fontSize="small" color="action" />
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {isSignature ? 'Add signature' : 'Upload file'}
            </Typography>
          </InlineDropZone>
        )}
      </Box>
    );
  }

  // Edit mode
  return (
    <Box className={className}>
      {fileInput}
      {/* Signature pad placeholder */}
      {isSignature && (
        <Box sx={{ mb: 2 }}>
          {files[0] ? (
            <SignaturePad $isDark={isDark} $neonAccent={neonAccent} $size={size}>
              <img src={files[0].url} alt="Signature" />
            </SignaturePad>
          ) : (
            <SignaturePad
              $isDark={isDark}
              $neonAccent={neonAccent}
              $size={size}
              sx={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
              onClick={handleClick}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Click to upload signature image
              </Typography>
            </SignaturePad>
          )}
          {files[0] && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <IconButton size="small" onClick={() => handleRemove(files[0].id)} disabled={disabled}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      )}
      {/* Drop zone for FILE and IMAGE types */}
      {!isSignature && (
        <>
          <DropZone
            $isDark={isDark}
            $neonAccent={neonAccent}
            $hasError={!!(error || validationError)}
            $isDragActive={isDragActive}
            $size={size}
            $disabled={disabled || field.disabled || false}
            onClick={handleClick}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {isUploading ? (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Uploading...
              </Typography>
            ) : (
              <>
                <CloudUploadIcon
                  sx={{
                    fontSize: size === 'large' ? 48 : size === 'medium' ? 40 : 32,
                    color: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.3)',
                    mb: 1,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    textAlign: 'center',
                  }}
                >
                  {isDragActive
                    ? 'Drop files here'
                    : isImage
                      ? 'Drag & drop an image or click to browse'
                      : 'Drag & drop files or click to browse'}
                </Typography>
                {effectiveMaxSize && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      mt: 0.5,
                    }}
                  >
                    Max size: {formatBytes(effectiveMaxSize)}
                  </Typography>
                )}
              </>
            )}
          </DropZone>

          {/* Uploaded files list */}
          {files.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {files.map((file) => (
                <FileItem key={file.id} $isDark={isDark} $neonAccent={neonAccent}>
                  {renderFileIcon(file)}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {file.name}
                    </Typography>
                    {file.size && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {formatBytes(file.size)}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(file.id)}
                    disabled={disabled}
                    sx={{
                      color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                      '&:hover': {
                        color: theme.palette.error.main,
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </FileItem>
              ))}
            </Box>
          )}
        </>
      )}
      {/* Field label and helper text */}
      {field.label && mode === 'edit' && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            mt: 1,
            display: 'block',
          }}
        >
          {field.label}
          {field.required && ' *'}
        </Typography>
      )}
      {(error || validationError || field.helpText) && (
        <FormHelperText error={!!(error || validationError)} sx={{ mt: 1 }}>
          {error || validationError || field.helpText}
        </FormHelperText>
      )}
    </Box>
  );
};

FileRenderer.displayName = 'FileRenderer';

export default FileRenderer;
